import { useMemoizedFn } from "ahooks";
import { useEffect, useMemo, useState } from "react";
import type { Observer, Observable } from "rxjs";

export function useObservable<T>(observable: Observable<T>, initialValue: T) {
  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    const subscription = observable.subscribe(setValue);
    return () => subscription.unsubscribe();
  }, [observable]);
  return value;
}

export function useSubscribe<T>(
  ob: Observable<T> | (() => Observable<T> | undefined),
  deps: unknown[]
): void;
export function useSubscribe<T>(
  ob: Observable<T> | (() => Observable<T> | undefined),
  observer: Partial<Observer<T>> | ((value: T) => void),
  deps: unknown[]
): void;
export function useSubscribe<T>(
  ob: Observable<T> | (() => Observable<T> | undefined),
  arg1: Partial<Observer<T>> | ((value: T) => void) | unknown[],
  arg2?: unknown[]
) {
  const { observerOrNext, deps } = useMemo(() => {
    if (Array.isArray(arg1)) {
      return {
        observerOrNext: undefined,
        deps: arg1,
      };
    }

    return {
      observerOrNext: arg1,
      deps: arg2,
    };
  }, [arg1, arg2]);

  const observer_ = useMemo(() => {
    if (typeof observerOrNext === "function") {
      return {
        next: observerOrNext,
        error: () => {
          console.error("error");
        },
      } as Partial<Observer<T>>;
    }

    return observerOrNext;
  }, [observerOrNext]);

  const next_ = useMemoizedFn((v: T) => observer_?.next?.(v));
  const complete_ = useMemoizedFn(() => observer_?.complete?.());
  const error_ = useMemoizedFn((e: unknown) => observer_?.error?.(e));

  useEffect(() => {
    const ob_ = typeof ob === "function" ? ob() : ob;
    const subscription = ob_?.subscribe({
      next: next_,
      complete: complete_,
      error: error_,
    });
    return () => subscription?.unsubscribe();
  }, deps);
}
